// RevXcel build 20260306-200601
import React, { useState, useEffect } from "react";
import {
  Database, Brain, BarChart2, Activity,
  RefreshCw, Settings, TrendingUp, Target,
  Home, BookOpen, ShieldAlert
} from "lucide-react";

// Shared
import {
  FontStyle, DEFAULT, deepMerge, loadS, saveS, ErrorBoundary, PROXY, resolveF, fmtM, slimDeals
} from "./shared/constants";
import { fetchDeals, fetchWonDeals, fetchOwners, calcMetrics } from "./shared/hubspotHelpers";
import { getPeriodDateRange, getPeriodTarget, applyFilters, FILTER_DEFAULTS, getQuickRange, FilterBar, OwnerMultiSelect } from "./shared/components";

// Auth
import { supabase } from "./lib/supabaseClient";
import LoginScreen from "./screens/LoginScreen";
import AcceptInvite    from "./screens/AcceptInvite";
import AdminScreen     from "./screens/AdminScreen";

// Screens
import SettingsScreen    from "./screens/SettingsScreen";
import PlanOfRecord      from "./screens/PlanOfRecord";

import RepCoaching       from "./screens/RepCoaching";
import AccountCoverage   from "./screens/AccountCoverage";
import PlanBuilder       from "./screens/AnnualPlan";
import CRMHealth         from "./screens/CRMHealth";
import MorningHome       from "./screens/MorningHome";
import LandingPage       from "./screens/LandingPage";

const HS_CACHE_TTL = 4 * 60 * 60 * 1000; // 4 hours
let _acCache = (() => {
  try {
    const c = localStorage.getItem("rx_companies");
    if (c) {
      const { data, ts } = JSON.parse(c);
      if (data?.length && Date.now() - ts < HS_CACHE_TTL) return { companies: data, loaded: true, token: "" };
    }
  } catch(e) {}
  return { companies: [], loaded: false, token: "" };
})();

const NAV_ALL = [
  {id:"home",        label:"Home",             icon:Home,       roles:["seg","admin","core","readonly"]},
  {id:"dashboard",   label:"Plan of Record",   icon:BarChart2,  roles:["seg","admin","core","readonly"]},
  {id:"coaching",    label:"Rep Coaching",     icon:TrendingUp, roles:["seg","admin","core","readonly"]},
  {id:"coverage",    label:"Account Coverage", icon:Target,     roles:["seg","admin","core","readonly"]},
  {id:"crm",         label:"CRM Health",       icon:Database,   roles:["seg","admin","core","readonly"]},
  {id:"planbuilder", label:"Setup",            icon:BookOpen,   roles:["seg","admin"]},
  {id:"admin",       label:"Admin",            icon:ShieldAlert,roles:["seg"]},
];

const TITLES = {
  home:       {t:"Home",               s:"Morning Briefing · Team Performance · Pipeline Snapshot"},
  dashboard:  {t:"Plan of Record",     s:"Live vs Plan · Pacing · Capacity · Priorities"},
  
  coaching:   {t:"Rep Coaching",       s:"Health Scores · Flags · Pipeline · Notes"},
  coverage:   {t:"Account Coverage",   s:"Territory Penetration · Dark Accounts · Contact Reach"},
  planbuilder:{t:"Setup",              s:"HubSpot · Stage Mapping · Team · Revenue Targets"},
  team:       {t:"Admin",             s:"Team access · Billing"},
  admin:      {t:"Admin",             s:"Team access · Billing"},
  crm:        {t:"CRM Health",         s:"HubSpot Data Reliability"},
  settings:   {t:"Setup",             s:"Configure your workspace"},
};


export default function App() {
  const [screen,      setScreen]      = useState("home");
  const [settings,    setSettings]    = useState(DEFAULT);
  const [hsData,      setHsData]      = useState({ metrics: null, owners: [], rawDeals: null, rawWonDeals: null, loading: false, error: null });
  const [user,        setUser]        = useState(null);
  const [userRole,    setUserRole]    = useState(null);
  // Companies live at App level so navigation never destroys them
  const [companies,         setCompanies]         = useState([]);
  const [companiesLoaded,   setCompaniesLoaded]   = useState(false);
  const [companiesLoading,  setCompaniesLoading]  = useState(false);
  const [companiesCount,    setCompaniesCount]    = useState(0);
  const [companiesLoadStart,setCompaniesLoadStart]= useState(null);
  const stopCompaniesRef    = React.useRef(false);
  const companiesStaleRef   = React.useRef(false); // set by restoreFromCache when cached companies are stale
  const hasRestoredRef      = React.useRef(false);  // ensures restoreFromCache only runs once per session
  const [authLoading,   setAuthLoading]   = useState(true);
  // Cache metadata — shown in UI so users know how fresh the data is
  const [cacheInfo,     setCacheInfo]     = useState({ deals: null, companies: null }); // ISO timestamps
  const [dataSource,    setDataSource]    = useState("loading"); // "cache" | "live" | "loading"
  const [showLanding, setShowLanding] = useState(false);
  const [filters,     setFilters]     = useState(FILTER_DEFAULTS);
  const [filterOpen,  setFilterOpen]  = useState(false);

  // ── POR period (must be before early returns) ──
  const [porPeriod, setPorPeriod] = useState("annual");

  // ── Computed filtered data (must be before early returns) ──
  const filteredScreens = ["crm"].includes(screen);
  const filteredDeals = React.useMemo(() => {
    if (!hsData.rawDeals) return [];
    const hasDateFilter = filters.dateFrom || filters.dateTo;
    const hasOwnerFilter = filters.ownerIds.length > 0;
    if (!hasDateFilter && !hasOwnerFilter) return hsData.rawDeals;
    return applyFilters(hsData.rawDeals, filters, settings);
  }, [hsData.rawDeals, filters]);

  const filteredMetrics = React.useMemo(() => {
    if (!hsData.rawDeals) return hsData.metrics;
    const hasDateFilter = filters.dateFrom || filters.dateTo;
    const hasOwnerFilter = filters.ownerIds.length > 0;
    if (!hasDateFilter && !hasOwnerFilter) return hsData.metrics;
    const _F = resolveF(settings);
    return calcMetrics(filteredDeals, filteredDeals.filter(d => {
      const s = d.properties[_F.dealstage]?.toLowerCase() || "";
      const mappings = settings.stages?.mappings || [];
      const wonIds = mappings.filter(m=>m.type==="won").map(m=>m.id.toLowerCase());
      return wonIds.length ? wonIds.includes(s) : s === "closedwon";
    }), settings);
  }, [filteredDeals, settings, hsData.metrics, hsData.rawDeals, filters]);

  const periodRange = getPeriodDateRange(porPeriod, +settings.company?.fiscalYear);
  const periodTarget = getPeriodTarget(porPeriod, settings.revenue?.target, settings.revenue?.monthlyTargets);
  const periodDeals = React.useMemo(() => {
    if (!hsData.rawDeals) return [];
    return applyFilters(hsData.rawDeals, { ...FILTER_DEFAULTS, dateField: "closedate", dateFrom: periodRange.from, dateTo: periodRange.to }, settings);
  }, [hsData.rawDeals, porPeriod, settings.company?.fiscalYear]);
  const periodMetrics = React.useMemo(() => {
    if (!periodDeals.length && !hsData.rawDeals) return hsData.metrics;
    const _F = resolveF(settings);
    const wonDeals = periodDeals.filter(d => {
      const s = d.properties[_F.dealstage]?.toLowerCase() || "";
      const mappings = settings.stages?.mappings || [];
      const wonIds = mappings.filter(m=>m.type==="won").map(m=>m.id.toLowerCase());
      return wonIds.length ? wonIds.includes(s) : s === "closedwon";
    });
    const periodSettings = { ...settings, revenue: { ...settings.revenue, target: String(periodTarget) } };
    return calcMetrics(periodDeals, wonDeals, periodSettings);
  }, [periodDeals, settings, periodTarget]);

  // ── Auth — check Supabase session on mount, listen for changes ──
  useEffect(() => {
    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        const u = { id: session.user.id, email: session.user.email };
        setUser(u);
        window._rxUserId = u.id;
        // Load role from org_members
        fetch(`/api/invite?action=list_members&org_id=00000000-0000-0000-0000-000000000001`)
          .then(r => r.json())
          .then(d => {
            const me = (d.members || []).find(m => m.user_id === u.id);
            setUserRole(me?.role || "readonly");
          })
          .catch(() => setUserRole("readonly"));
        // Load settings from Supabase
        fetch(`/api/supabase?action=get_settings&user_id=${u.id}`)
          .then(r => r.json())
          .then(data => {
            if (data && Object.keys(data).length > 0) {
              const savedToken = data.hubspot?.token;
              const localToken = loadS().hubspot?.token;
              const bestToken = (localToken?.startsWith("pat-")) ? localToken : (savedToken?.startsWith("pat-") ? savedToken : "");
              const cfg = deepMerge(DEFAULT, { ...data, hubspot: { ...DEFAULT.hubspot, ...data.hubspot, token: bestToken, configured: !!bestToken } });
              setSettings(cfg);
              restoreFromCache(u.id, cfg);
            } else {
              const ls = loadS();
              setSettings(deepMerge(DEFAULT, ls));
              restoreFromCache(u.id, deepMerge(DEFAULT, ls));
            }
          })
          .catch(() => {})
          .finally(() => setAuthLoading(false));
      } else {
        setAuthLoading(false);
      }
    });

    // Listen for login/logout events
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser({ id: session.user.id, email: session.user.email });
        setShowLanding(false);
      } else {
        setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // ── Load HubSpot data — fetches live from HubSpot, then saves to Supabase ──
  const loadHubSpotData = React.useCallback(async (s) => {
    const cfg = s || settings;
    if (!cfg.hubspot?.token) return;
    setHsData(prev => ({ ...prev, loading: true, error: null }));
    setDataSource("loading");
    try {
      const [deals, wonDeals, owners] = await Promise.all([
        fetchDeals(cfg.hubspot.token, cfg),
        fetchWonDeals(cfg.hubspot.token, cfg),
        fetchOwners(cfg.hubspot.token),
      ]);
      const newHsData = { metrics: calcMetrics(deals, wonDeals, cfg), owners, rawDeals: deals, rawWonDeals: wonDeals, loading: false, error: null };
      setHsData(newHsData);
      setDataSource("live");

      // ── Persist everything to Supabase in the background ──────────────
      const uid = user?.id || cfg.hubspot?.userId;
      if (uid) {
        const computed = newHsData.metrics;
        const cacheableMetrics = {
          pipeline: computed.pipeline, coverage: computed.coverage, gap: computed.gap,
          winRate: computed.winRate, avgDealSize: computed.avgDealSize,
          stale: computed.stale, staleRate: computed.staleRate,
          ytdBookings: computed.ytdBookings, allTimeWonBookings: computed.allTimeWonBookings,
          totalDeals: computed.totalDeals, won: computed.won, lost: computed.lost,
          crmScore: computed.crmScore, crmAreas: computed.crmAreas,
          fieldUsagePct: computed.fieldUsagePct, completePct: computed.completePct,
          ownerPct: computed.ownerPct, contactsPct: computed.contactsPct,
          nextStepPct: computed.nextStepPct, sandbaggingRate: computed.sandbaggingRate,
          ghostWinRate: computed.ghostWinRate, stuckRate: computed.stuckRate,
          repMetrics: computed.repMetrics, segmentMetrics: computed.segmentMetrics,
          stageMap: computed.stageMap, timeLenses: computed.timeLenses,
          cachedRepStats: computed.cachedRepStats, recentWins: computed.recentWins || [],
        };
        const now = new Date().toISOString();
        // Save metrics (always small — fire and forget)
        fetch("/api/supabase?action=save_metrics", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: uid, metrics: cacheableMetrics, owners }),
        }).catch(e => console.warn("Metrics cache save failed:", e.message));

        // Save deals — chunked client-side (500 per request) to stay under
        // Vercel's 4.5MB body limit. Open and won deals sent separately.
        const saveDealsChunked = async () => {
          const CHUNK = 150; // ~150 slim deals ≈ 200-400KB — well under Vercel 4.5MB limit
          const saveType = async (dealArr, deal_type) => {
            // Slim deals before sending — strip all properties not in field mapping
            const slim = slimDeals(dealArr);
            const pages = [];
            for (let i = 0; i < slim.length; i += CHUNK) pages.push(slim.slice(i, i + CHUNK));
            if (pages.length === 0) pages.push([]);
            for (let page = 0; page < pages.length; page++) {
              const r = await fetch("/api/supabase?action=save_deals", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  user_id: uid, deal_type, page,
                  deals: pages[page],
                  is_first: page === 0,
                }),
              });
              if (!r.ok) throw new Error(`save_deals ${deal_type} p${page}: ${r.status}`);
            }
          };
          await saveType(deals, "open");
          await saveType(wonDeals, "won");
          setCacheInfo(prev => ({ ...prev, deals: now }));
        };
        saveDealsChunked().catch(e => console.warn("Deals cache save failed:", e.message));
      }
    } catch(e) {
      setHsData(prev => ({ ...prev, loading: false, error: e.message }));
      setDataSource("cache");
    }
  }, [settings, user?.id]);

  // ── Restore from Supabase cache on login ─────────────────────────────────
  // Called once after auth resolves. Loads deals + metrics from Supabase so
  // the user sees real data immediately, without waiting for a HubSpot sync.
  // After restoring, checks if cache is stale (>4h) and syncs in background.
  const restoreFromCache = React.useCallback(async (uid, cfg) => {
    if (!uid || !cfg?.hubspot?.token) return;
    if (hasRestoredRef.current) return; // already ran this session — don't re-fetch on re-render
    hasRestoredRef.current = true;

    try {
      // Load deals from Supabase
      const dealsRes = await fetch(`/api/supabase?action=get_deals&user_id=${uid}`);
      if (!dealsRes.ok) throw new Error(`get_deals ${dealsRes.status}`);
      const dealsData = await dealsRes.json();

      // Check for actual deal data — raw_deals must exist AND have records
      const hasCache = dealsData && Array.isArray(dealsData.raw_deals) && dealsData.raw_deals.length > 0;

      if (hasCache) {
        const { raw_deals, won_deals, updated_at } = dealsData;
        const metrics = calcMetrics(raw_deals, won_deals || [], cfg);
        // Load owners from metrics cache
        const metricsRes = await fetch(`/api/supabase?action=get_metrics&user_id=${uid}`);
        const metricsData = metricsRes.ok ? await metricsRes.json() : null;
        const owners = metricsData?.owners || [];
        setHsData({ metrics, owners, rawDeals: raw_deals, rawWonDeals: won_deals || [], loading: false, error: null });
        setCacheInfo(prev => ({ ...prev, deals: updated_at }));
        setDataSource("cache");
        console.log(`Restored ${raw_deals.length} open + ${(won_deals||[]).length} won deals from cache (${updated_at})`);
        // Don't auto-refresh even if stale — user controls sync via the button
      } else {
        // No cache in Supabase yet — user needs to click Load Deals once
        console.log("No deal cache found — click Load Deals to sync from HubSpot");
        setDataSource("cache"); // don't show loading state
      }
    } catch (e) {
      console.warn("Cache restore failed:", e.message);
      // Don't auto-fetch — leave data empty, user clicks Load Deals
      setDataSource("cache");
    }

    // Load companies from Supabase (separate — large)
    try {
      const repOwnerIds = (cfg?.team?.reps || []).map(r => r.hubspotOwnerId).filter(Boolean);
      const fingerprint = repOwnerIds.slice().sort().join(",");
      const compRes = await fetch(
        `/api/supabase?action=get_companies&user_id=${uid}&owner_fingerprint=${encodeURIComponent(fingerprint)}`
      );
      const compData = await compRes.json();

      if (compData && compData.companies && !compData.stale) {
        setCompanies(compData.companies);
        setCompaniesLoaded(true);
        setCompaniesCount(compData.companies.length);
        setCacheInfo(prev => ({ ...prev, companies: compData.updated_at }));
        // Stale after 24h for companies (changes slowly).
        // Don't call loadCompanies here — it's defined after restoreFromCache.
        // The stale-company useEffect below handles background refresh.
        const ageMs = compData.updated_at ? Date.now() - new Date(compData.updated_at).getTime() : Infinity;
        if (ageMs > 24 * 60 * 60 * 1000) {
          console.log("Company cache stale — will background refresh");
          companiesStaleRef.current = true; // triggers the useEffect below to re-fetch
          setCompaniesLoaded(false); // causes useEffect to run
        }
      }
    } catch (e) {
      console.warn("Company cache restore failed:", e.message);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── App-level company loader — survives navigation ──────────────────────
  const loadCompanies = React.useCallback(async (token) => {
    if (!token || companiesLoading) return;

    // Require at least one rep with a linked HubSpot owner ID
    const repOwnerIds = (settings?.team?.reps || [])
      .map(r => r.hubspotOwnerId).filter(Boolean);

    if (repOwnerIds.length === 0) {
      alert("No reps are linked to HubSpot owners yet.\n\nGo to Setup → Quota & Team and link each rep to their HubSpot Owner before loading accounts.");
      return;
    }

    stopCompaniesRef.current = false;
    window._stopCompanies = () => { stopCompaniesRef.current = true; };
    setCompanies([]);
    setCompaniesCount(0);
    setCompaniesLoaded(false);
    setCompaniesLoading(true);
    setCompaniesLoadStart(Date.now());
    try {
      const sleep = ms => new Promise(r => setTimeout(r, ms));

      const ownerParam = `&ownerIds=${encodeURIComponent(repOwnerIds.join(","))}`;

      // No hard cap — load all accounts assigned to configured reps.
      let all = [], after = null;
      while (true) {
        if (stopCompaniesRef.current) break;
        const url = `${PROXY}?endpoint=companies_search${ownerParam}${after ? `&after=${encodeURIComponent(after)}` : ""}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (res.status === 429) { await sleep(5000); continue; }
        if (!res.ok) throw new Error(`HubSpot API error: ${res.status}`);
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        const page = data.results || [];
        all = [...all, ...page];
        setCompaniesCount(all.length);
        after = data.paging?.next?.after || null;
        if (!after) break;
        await sleep(150);
      }
      setCompanies(all);
      setCompaniesLoaded(true);
      const ownerFingerprint = repOwnerIds.slice().sort().join(",");
      _acCache = { companies: all, loaded: true, token, ownerFingerprint };
      try {
        localStorage.setItem("rx_companies", JSON.stringify({ data: all, ts: Date.now(), ownerFingerprint }));
      } catch(e) {}
      const uid = user?.id;
      if (uid) {
        const now = new Date().toISOString();
        const CHUNK = 500;
        const saveChunked = async () => {
          for (let page = 0; page * CHUNK < all.length; page++) {
            const slice = all.slice(page * CHUNK, (page + 1) * CHUNK);
            await fetch("/api/supabase?action=save_companies", {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                user_id: uid,
                companies: slice,
                owner_fingerprint: ownerFingerprint,
                page,
                is_first: page === 0,
                is_last:  (page + 1) * CHUNK >= all.length,
              }),
            });
          }
          setCacheInfo(prev => ({ ...prev, companies: now }));
        };
        saveChunked().catch(e => console.warn("Company Supabase save failed:", e.message));
      }
    } catch(e) {
      console.error("loadCompanies failed:", e.message);
    } finally {
      setCompaniesLoading(false);
      setCompaniesLoadStart(null);
    }
  }, [user?.id, companiesLoading, settings?.team?.reps]);

  // Re-fetch companies only when restoreFromCache explicitly marked them stale.
  // Does NOT fire on cold first visit — user still clicks "Load Accounts" then.
  React.useEffect(() => {
    if (companiesStaleRef.current && !companiesLoading && settings?.hubspot?.token) {
      companiesStaleRef.current = false;
      loadCompanies(settings.hubspot.token);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companiesLoaded, companiesLoading]);

  const globalRefresh = React.useCallback(() => {
    _acCache = { companies: [], loaded: false, token: "" };
    setCompanies([]); setCompaniesLoaded(false); setCompaniesCount(0);
    setHsData({ metrics: null, owners: [], rawDeals: null, rawWonDeals: null, loading: false, error: null });
    loadHubSpotData();
  }, [loadHubSpotData]);

  // ── Manual load only — buttons in sidebar ──

  // ── Manual load only — buttons in sidebar ──

  // ── Debounced Supabase save (1.5s after last trigger) ──
  const _sbTimer = React.useRef(null);
  const saveToSupabase = React.useCallback((ns, uid) => {
    if (!uid) return;
    if (_sbTimer.current) clearTimeout(_sbTimer.current);
    _sbTimer.current = setTimeout(async () => {
      try {
        const res = await fetch("/api/supabase?action=save_settings", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: uid, data: ns }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          console.warn("Supabase save failed:", err.error || res.status, "— settings still saved to localStorage");
        } else {
          console.log("Supabase save: OK");
        }
      } catch(e) { console.warn("Supabase save network error:", e.message); }
    }, 1500);
  }, []);

  // ── Save settings (localStorage immediate, Supabase debounced) ──
  const handleSave = async (ns) => {
    setSettings(ns);
    saveS(ns);
    saveToSupabase(ns, user?.id);
    // Recalc metrics from already-loaded deals if available — no new HubSpot request
    if (ns.hubspot?.token && hsData.rawDeals) {
      const newMetrics = calcMetrics(hsData.rawDeals, hsData.rawWonDeals || [], ns);
      setHsData(prev => ({ ...prev, metrics: newMetrics }));
    }
  };

  // ── Logout ──
  const handleLogout = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem("rx_user");
    setUser(null);
  };

  const goToSettings = () => setScreen("planbuilder");
  const done = [
    settings.hubspot?.configured,       // HubSpot connected
    settings.stages?.configured,        // Stage mapping
    settings.fieldMap?.configured,      // Field mapping
    !!settings.company?.name,           // Company info
    settings.revenue?.configured,       // Revenue target
    settings.team?.configured,          // Quota & team
    settings.segments?.configured,      // Revenue matrix
    settings.targets?.configured,       // Performance targets
    settings.nrr?.configured,           // Retention
    settings.funnel?.configured,        // GTM assumptions
  ].filter(Boolean).length;
  const TOTAL_STEPS = 10;

  // ── Accept invite route ──
  if (window.location.pathname === "/accept-invite") {
    return <AcceptInvite />;
  }

  // Loading state
  if (authLoading) return (
    <div style={{height:"100vh",background:"#F8F8F8",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:16}}>
      <FontStyle/>
      <div style={{width:40,height:40,background:"linear-gradient(135deg,#CC1A1A,#991B1B)",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,fontWeight:800,color:"#FFFFFF",fontFamily:"'DM Mono',monospace"}}>RX</div>
      <div style={{fontSize:13,color:"#555555"}}>Loading…</div>
    </div>
  );

  // Not logged in — show RevXcel login
  if (!user) return <LoginScreen onLogin={(session) => {
    setUser({ id: session.user.id, email: session.user.email });
    setAuthLoading(false);
    setShowLanding(false);
  }} />;

  // Logged in but not a member of any org — show no access screen
  if (userRole !== null && !["seg","admin","core","readonly"].includes(userRole)) {
    return (
      <div style={{ minHeight:"100vh", background:"#F8F8F8", display:"flex",
        alignItems:"center", justifyContent:"center", fontFamily:"'DM Sans',sans-serif" }}>
        <div style={{ textAlign:"center", maxWidth:380, padding:"0 20px" }}>
          <div style={{ fontSize:40, marginBottom:16 }}>🔒</div>
          <div style={{ fontSize:18, fontWeight:800, color:"#111", marginBottom:8 }}>No access</div>
          <div style={{ fontSize:13, color:"#555", lineHeight:1.6, marginBottom:24 }}>
            Your account isn't linked to a RevXcel workspace yet. Contact your administrator for an invite.
          </div>
          <button onClick={() => supabase.auth.signOut()}
            style={{ padding:"10px 24px", background:"#CC1A1A", border:"none", borderRadius:8,
              color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" }}>
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  const filteredHsData = { ...hsData, metrics: filteredMetrics };
  const porHsData = { ...hsData, metrics: periodMetrics };

  const wrap = (el) => <ErrorBoundary>{el}</ErrorBoundary>;
  const renderScreen = () => {
    switch(screen) {
      case "home":        return wrap(<MorningHome settings={settings} hsData={hsData} onNavigate={setScreen}/>);
      case "dashboard":   return wrap(<PlanOfRecord settings={settings} hsData={hsData} onGo={goToSettings} onSave={handleSave}/>);
      // POR Builder removed — merged into Plan of Record
      case "por":         return wrap(<PlanOfRecord settings={settings} hsData={hsData} onGo={goToSettings} onSave={handleSave}/>);
      case "coaching":    return wrap(<RepCoaching settings={settings} hsData={hsData} onGo={goToSettings} userId={user?.id}/>);
      case "coverage":    return wrap(<AccountCoverage settings={settings} hsData={hsData} companies={companies} companiesLoaded={companiesLoaded} companiesLoading={companiesLoading} companiesCount={companiesCount} companiesLoadStart={companiesLoadStart} onLoadCompanies={()=>loadCompanies(settings.hubspot?.token)}/>);
      case "planbuilder": return wrap(<PlanBuilder settings={settings} onSave={handleSave} hsData={hsData} userId={user?.id}/>);
      case "team":
      case "admin":       return wrap(<AdminScreen user={user} userId={user?.id} settings={settings} onSave={handleSave} orgId="00000000-0000-0000-0000-000000000001"/>);
      case "crm":         return wrap(<CRMHealth settings={settings} onGo={goToSettings} metrics={filteredMetrics} companies={companies}/>);
      case "settings":    return <SettingsScreen settings={settings} onSave={handleSave}/>;
      default:            return wrap(<MorningHome settings={settings} hsData={hsData} onNavigate={setScreen}/>);
    }
  };

  return (
    <div style={{display:"flex",height:"100vh",background:"#F8F8F8",overflow:"hidden"}}>
      <FontStyle/>
      <div style={{width:230,background:"#FFFFFF",borderRight:"1px solid rgba(0,0,0,0.08)",display:"flex",flexDirection:"column",flexShrink:0}}>
        <div style={{padding:"18px 16px 14px",borderBottom:"1px solid rgba(0,0,0,0.08)"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
            <div style={{width:36,height:36,background:"linear-gradient(135deg,#CC1A1A,#991B1B)",borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:"#FFFFFF",fontFamily:"'DM Mono',monospace"}}>RX</div>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:"#111111"}}>RevXcel</div>
              <div style={{fontSize:10,color:"#555555"}}>RevOps Platform</div>
            </div>
          </div>
          <div style={{background:"#F0F0F0",borderRadius:8,padding:"9px 11px"}}>
            {settings.company.name ? <>
              <div style={{fontSize:9,color:"#555555",marginBottom:2,textTransform:"uppercase",letterSpacing:"0.07em"}}>Active Client</div>
              <div style={{fontSize:12,fontWeight:700,color:"#111111",marginBottom:4}}>{settings.company.name}</div>
              <div style={{display:"flex",alignItems:"center",gap:5}}>
                <div className="live" style={{width:6,height:6,borderRadius:"50%",background:"#16A34A"}}/>
                <span style={{fontSize:10,color:"#16A34A"}}>{settings.company?.crm} · {done}/{TOTAL_STEPS} complete</span>
              </div>
            </> : <>
              <div style={{fontSize:11,fontWeight:600,color:"#D97706",marginBottom:4}}>⚙️ Setup Required</div>
              <div style={{fontSize:10,color:"#555555"}}>Go to Settings to configure your POR</div>
            </>}
          </div>
        </div>

        {/* ── Data load buttons ── */}
        <div style={{padding:"8px 12px",borderBottom:"1px solid rgba(0,0,0,0.06)",display:"flex",flexDirection:"column",gap:5}}>

          {/* Cache freshness indicator */}
          {cacheInfo.deals && dataSource === "cache" && (
            <div style={{fontSize:9,color:"#aaa",marginBottom:4,textAlign:"center",fontFamily:"'DM Mono',monospace"}}>
              {(()=>{
                const mins = Math.round((Date.now()-new Date(cacheInfo.deals).getTime())/60000);
                return mins < 60 ? `cached ${mins}m ago` : `cached ${Math.round(mins/60)}h ago`;
              })()}
              {" · "}
              <span onClick={()=>loadHubSpotData()} style={{color:"#CC1A1A",cursor:"pointer",textDecoration:"underline"}}>sync</span>
            </div>
          )}
          <button onClick={()=>loadHubSpotData()} disabled={hsData.loading}
            style={{width:"100%",display:"flex",alignItems:"center",gap:6,padding:"7px 10px",borderRadius:7,
              border:"1px solid rgba(0,0,0,0.08)",background:"transparent",
              color:hsData.loading?"#aaa":"#555",fontSize:11,fontWeight:600,
              cursor:hsData.loading?"not-allowed":"pointer",transition:"all 0.15s"}}
            onMouseEnter={e=>{if(!hsData.loading){e.currentTarget.style.background="rgba(204,26,26,0.06)";e.currentTarget.style.color="#CC1A1A";}}}
            onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color=hsData.loading?"#aaa":"#555";}}>
            <RefreshCw size={11} style={{flexShrink:0,animation:hsData.loading?"spin 1s linear infinite":"none"}}/>
            <span style={{flex:1,textAlign:"left"}}>
              {hsData.loading?"Loading deals…":hsData.rawDeals?`Deals · ${hsData.rawDeals.length.toLocaleString()}`:"Load Deals / Opps"}
            </span>
          </button>

          <button onClick={()=>loadCompanies(settings.hubspot?.token)} disabled={companiesLoading}
            style={{width:"100%",display:"flex",alignItems:"center",gap:6,padding:"7px 10px",borderRadius:7,
              border:"1px solid rgba(0,0,0,0.08)",background:"transparent",
              color:companiesLoading?"#aaa":"#555",fontSize:11,fontWeight:600,
              cursor:companiesLoading?"not-allowed":"pointer",transition:"all 0.15s"}}
            onMouseEnter={e=>{if(!companiesLoading){e.currentTarget.style.background="rgba(124,58,237,0.06)";e.currentTarget.style.color="#7C3AED";}}}
            onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color=companiesLoading?"#aaa":"#555";}}>
            <Database size={11} style={{flexShrink:0}}/>
            <span style={{flex:1,textAlign:"left"}}>
              {companiesLoading?`Accounts · ${companiesCount.toLocaleString()} loaded…`:companiesLoaded?`Accounts · ${companies.length.toLocaleString()}`:"Load Accounts"}
            </span>
          </button>


          {hsData.cachedAt&&(
            <div style={{fontSize:9,color:"#aaa",textAlign:"center",marginTop:2}}>
              Last synced: {(()=>{
                const d=new Date(hsData.cachedAt);
                const diff=Math.round((Date.now()-d.getTime())/60000);
                if(diff<1) return "just now";
                if(diff<60) return `${diff}m ago`;
                if(diff<1440) return `${Math.round(diff/60)}h ago`;
                return d.toLocaleDateString();
              })()}
            </div>
          )}
        </div>

        {/* Main nav — core screens only */}
        <nav style={{flex:1,padding:"10px 8px",overflowY:"auto"}}>
          {NAV_ALL.filter(item => !userRole || item.roles.includes(userRole))
            .filter(item => !["planbuilder","admin"].includes(item.id))
            .map(item=>{
            const active=screen===item.id;
            const Icon=item.icon;
            return (
              <button key={item.id} onClick={()=>setScreen(item.id)}
                style={{display:"flex",alignItems:"center",gap:9,width:"100%",padding:"9px 11px",borderRadius:7,border:"none",cursor:"pointer",marginBottom:2,textAlign:"left",transition:"all 0.15s",background:active?"rgba(204,26,26,0.08)":"transparent",borderLeft:active?"2px solid #CC1A1A":"2px solid transparent",color:active?"#CC1A1A":"#555555"}}>
                <Icon size={15}/>
                <span style={{fontSize:12,fontWeight:active?700:500}}>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Bottom — Setup, Admin, user info */}
        <div style={{padding:"8px",borderTop:"1px solid rgba(0,0,0,0.08)"}}>
          {NAV_ALL.filter(item => !userRole || item.roles.includes(userRole))
            .filter(item => ["planbuilder","admin"].includes(item.id))
            .map(item => {
              const active = screen===item.id;
              const Icon = item.icon;
              return (
                <button key={item.id} onClick={()=>setScreen(item.id)}
                  style={{display:"flex",alignItems:"center",gap:9,width:"100%",padding:"9px 11px",borderRadius:7,border:"none",cursor:"pointer",marginBottom:2,textAlign:"left",transition:"all 0.15s",background:active?"rgba(204,26,26,0.08)":"transparent",borderLeft:active?"2px solid #CC1A1A":"2px solid transparent",color:active?"#CC1A1A":"#555555"}}>
                  <Icon size={15}/>
                  <span style={{fontSize:12,fontWeight:active?700:500}}>{item.label}</span>
                </button>
              );
            })
          }
          <div style={{display:"flex",alignItems:"center",gap:8,margin:"6px 0",padding:"8px 10px",background:"#F0F0F0",borderRadius:8}}>
            <div style={{width:28,height:28,borderRadius:7,background:"linear-gradient(135deg,#7C3AED,#7C3AED)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"white",flexShrink:0}}>
              {(user.email||"U")[0].toUpperCase()}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:11,fontWeight:600,color:"#111111",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user.email}</div>
              <div style={{fontSize:9,color:"#555555",textTransform:"capitalize"}}>{userRole||"user"}</div>
            </div>
          </div>
          <button onClick={handleLogout}
            style={{width:"100%",background:"transparent",border:"1px solid rgba(0,0,0,0.08)",borderRadius:7,padding:"7px 12px",color:"#555555",fontSize:11,cursor:"pointer",textAlign:"center"}}>
            Sign out
          </button>
        </div>
      </div>

      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        <div style={{background:"#FFFFFF",borderBottom:"1px solid rgba(0,0,0,0.08)",padding:"12px 22px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div>
            <h1 style={{fontSize:16,fontWeight:700,color:"#111111"}}>{(TITLES[screen]||TITLES.home).t}</h1>
            <p style={{fontSize:11,color:"#555555",marginTop:1}}>{settings.company.name||"RevXcel"} · {(TITLES[screen]||TITLES.home).s}</p>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            {done<TOTAL_STEPS&&screen!=="planbuilder"&&(
              <button onClick={goToSettings} style={{display:"flex",alignItems:"center",gap:6,background:"rgba(217,119,6,0.12)",border:"1px solid rgba(217,119,6,0.12)",borderRadius:7,padding:"5px 12px",color:"#D97706",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                <Settings size={12}/> {TOTAL_STEPS-done} sections to complete
              </button>
            )}
            <div onClick={goToSettings}
              style={{width:32,height:32,borderRadius:7,background:"linear-gradient(135deg,#7C3AED,#7C3AED)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:"white",fontFamily:"'DM Mono',monospace",cursor:"pointer"}}
              title="Settings">
              {user?.email?.[0]?.toUpperCase()||"RX"}
            </div>
          </div>
        </div>
        {/* Global Filter Bar — shown on filterable screens */}
        {filteredScreens && hsData.rawDeals && (
          <FilterBar
            filters={filters}
            setFilters={setFilters}
            owners={hsData.owners || []}
            settings={settings}
            visible={filterOpen}
            onToggle={() => setFilterOpen(o => !o)}
          />
        )}
        <div style={{flex:1,overflowY:"auto",padding:"18px 22px"}} className="rx-scroll">
          {renderScreen()}
        </div>
      </div>
    </div>
  );
}

